import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { colors } from "sexy";
import { EmailSent, Org, Project } from "types";
import { CustomImage } from "@components/ui/images/CustomImage";
import { CustomDocument } from "@components/ui/documents/CustomDocument";
import { getLocalFromFlutter } from "@services/timeHelpers";
import { emailsSentApi } from "@services/api/emailsSent";
import { useEffect } from "react";

interface EmailSentModalProps {
  onClose: () => void;
  email_sent_id?: string;
  sentEmails?: EmailSent[];
  activeProject?: Project;
  orgData?: Org;
  onReply?: (email: EmailSent) => void;
}

export const EmailSentModal = ({
  onClose,
  email_sent_id,
  sentEmails = [],
  activeProject,
  orgData,
  onReply,
}: EmailSentModalProps) => {
  const email_sent = sentEmails.find((email) => email.id === email_sent_id);

  // Mark as read on open if unread
  useEffect(() => {
    if (email_sent && email_sent.is_read === false) {
      emailsSentApi.markAsRead(email_sent.id).catch(() => {});
      email_sent.is_read = true; // optimistic
    }
  }, [email_sent?.id]);

  if (!email_sent) {
    return null;
  }

  return (
    <BaseModal
      width="max-w-800"
      title="Email envoyé"
      startIcon={<Icon1 name="email" size={20} />}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        {/* Email Headers */}
        <div className="flex flex-col text-h2 gap-3">
          {/* From */}
          <div className="flex flex-row items-center">
            <div className="min-w-100 text-neutral-600">De</div>
            <div className="w-full py-2 font-medium">
              {orgData?.infos?.sender_email || "email inconnu"}
            </div>
          </div>

          {/* To */}
          <div className="flex flex-row items-start">
            <div className="min-w-100 text-neutral-600">À</div>
            <div className="w-full py-2 font-medium">
              {email_sent?.emails_to?.join(", ") || "email inconnu"}
            </div>
          </div>

          {/* CC */}
          {email_sent?.emails_cc && email_sent.emails_cc.length > 0 && (
            <div className="flex flex-row items-start">
              <div className="min-w-100 text-neutral-600">CC</div>
              <div className="w-full py-2 font-medium">
                {email_sent.emails_cc.join(", ")}
              </div>
            </div>
          )}

          {/* BCC */}
          {email_sent?.emails_bcc && email_sent.emails_bcc.length > 0 && (
            <div className="flex flex-row items-start">
              <div className="min-w-100 text-neutral-600">BCC</div>
              <div className="w-full py-2 font-medium">
                {email_sent.emails_bcc.join(", ")}
              </div>
            </div>
          )}

          {/* Date */}
          <div className="flex flex-row items-start">
            <div className="min-w-100 text-neutral-600">Date</div>
            <div className="w-full py-2 font-medium">
              {email_sent?.created_at ? getLocalFromFlutter(email_sent.created_at) : "Date inconnue"}
            </div>
          </div>

          {/* Subject */}
          <div
            className="rounded-lg p-3 mt-2"
            style={{ backgroundColor: colors.neutral_50 }}
          >
            <div className="text-sm font-semibold">
              {email_sent?.subject || "Aucun sujet"}
            </div>
          </div>
        </div>

        {/* Body */}
        <div
          className="rounded-lg p-3 mt-2"
          style={{ backgroundColor: colors.neutral_50 }}
        >
          <div className="text-sm whitespace-pre-wrap leading-5">
            {email_sent?.body || "Aucun contenu"}
          </div>
        </div>

        {/* Photos Preview */}
        {activeProject?.photos &&
          activeProject.photos.filter((p) =>
            email_sent?.photos_id?.includes(p.id),
          ).length > 0 && (
            <div
              className="flex flex-row gap-1.5 mt-2 flex-wrap items-center p-2 rounded-lg"
              style={{ backgroundColor: colors.neutral_50 }}
            >
              {activeProject.photos
                .filter((p) => email_sent?.photos_id?.includes(p.id))
                .map((photo, index) => (
                  <CustomImage
                    key={index}
                    source={{ uri: photo.file.url }}
                    alt={`Photo ${index + 1}`}
                    className="w-150 h-150 rounded-lg"
                    hidden={photo.hidden}
                  />
                ))}
            </div>
          )}

        {/* Document Attachments */}
        {email_sent?.files && email_sent.files.length > 0 && (
          <div className="flex flex-col gap-1.5 mt-2">
            {email_sent.files.map((doc, index) => (
              <CustomDocument
                key={index}
                name={doc.name}
                url={doc.url}
                fontSize={14}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        {onReply && (
          <Button
            variant="small"
            label="Répondre"
            onClick={() => onReply(email_sent)}
          />
        )}
        <SecondaryButton variant="small" onClick={onClose}>
          Fermer
        </SecondaryButton>
      </div>
    </BaseModal>
  );
};
